import { useContext, Fragment, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CheckIcon from "@mui/icons-material/Check";
import { SessionContext } from "../context/SessionContext";
import { NavbarContext } from "../context/NavbarContext";
import { useTranslation } from "react-i18next";
import { CircularProgress, Switch, FormControlLabel } from "@mui/material";

export default function APIKeyAccess() {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const navbar = useContext(NavbarContext);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [price, setPrice] = useState(0);
  const [fullYearlyPrice, setFullYearlyPrice] = useState(0);
  const [priceId, setPriceId] = useState("");

  const apiKeyMonthlyPriceUSD = 79;
  const apiKeyYearlyPriceUSD = 792;
  const apiKeyMonthlyPriceEUR = 75;
  const apiKeyYearlyPriceEUR = 756;

  const title = "🔑 " + t("landing.apiKeyAccess");
  const subheader = t("landing.provideApiKey");
  const highlighted = true;
  const action = "login";

  const description = [
    t("landing.maxBatch", { batch: "1,500" }),
    t("landing.maxSize", { size: "100MB" }),
    t("landing.maxVideoSize", { size: "2GB" }),
    t("landing.provideApiKeyDescription"),
  ];

  const buttonText = t("landing.subscribe");

  const handleBillingCycleToggle = () => {
    setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly");
  };

  // Calculate price based on currency and billing cycle
  useEffect(() => {
    const isEUR = session?.currentCurrency === "EUR";
    const isMonthly = billingCycle === "monthly";

    if (isEUR) {
      setPrice(isMonthly ? apiKeyMonthlyPriceEUR : apiKeyYearlyPriceEUR / 12);
    } else {
      setPrice(isMonthly ? apiKeyMonthlyPriceUSD : apiKeyYearlyPriceUSD / 12);
    }
  }, [session?.currentCurrency, billingCycle]);

  // Calculate full yearly price
  useEffect(() => {
    const isEUR = session?.currentCurrency === "EUR";
    setFullYearlyPrice(isEUR ? apiKeyYearlyPriceEUR : apiKeyYearlyPriceUSD);
  }, [session?.currentCurrency]);

  // Set price ID
  useEffect(() => {
    const isEUR = session?.currentCurrency === "EUR";
    const isMonthly = billingCycle === "monthly";

    if (isEUR) {
      setPriceId(
        isMonthly
          ? process.env.REACT_APP_API_KEY_MONTHLY_PRODUCT_EUR
          : process.env.REACT_APP_API_KEY_YEARLY_PRODUCT_EUR
      );
    } else {
      setPriceId(
        isMonthly
          ? process.env.REACT_APP_API_KEY_MONTHLY_PRODUCT_USD
          : process.env.REACT_APP_API_KEY_YEARLY_PRODUCT_USD
      );
    }
  }, [session?.currentCurrency, billingCycle]);

  const handleClick = () => {
    if (
      session?.enableExternalApiKey ||
      session?.subStatus?.[session?.subStatus?.length - 1]?.active
    ) {
      window.open(
        `${process.env.REACT_APP_STRIPE_URL}/p/login/3cs14t9l4deC7tu000`,
        "_blank"
      );
    } else if (session?.user?.id || session?.stashedUser?.id) {
      setLoading(true);
      session?.handlePayment(
        priceId,
        process.env.REACT_APP_STRIPE_DEFAULT_PROMO
      );
    } else {
      localStorage.setItem(
        "goToCheckout",
        JSON.stringify({
          price_id: priceId,
          coupon_id: process.env.REACT_APP_STRIPE_DEFAULT_PROMO,
        })
      );
      navbar.setSelectedNavItem(action);
      navbar.setOpenNavModal(true);
    }
  };

  return (
    <Fragment>
      <Grid item xs={12} md={4}>
        <Box
          sx={{
            bgcolor: "white",
            width: "100%",
            outline: `3px solid ${highlighted ? "#3535ff" : "lightgray"}`,
            transition: "all 150ms ease-in-out",
            borderRadius: "1rem",
            "&:hover": {
              outline: `${highlighted ? "6px" : "3px"} solid ${
                highlighted ? "#3535ff" : "lightgray"
              }`,
              scale: "1.01",
              cursor: "pointer",
            },
          }}
        >
          <Box
            sx={{
              p: "1.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Chip
                  label={subheader}
                  sx={{
                    bgcolor: "#dcdcff",
                    color: "#3535ff",
                    fontWeight: "bold",
                    borderRadius: "0.7rem",
                    fontSize: "0.9rem",
                    mb: 2,
                  }}
                />
                <Typography
                  component="h6"
                  variant="h6"
                  align="left"
                  fontWeight={"bold"}
                  sx={{
                    color: "black",
                    mb: 1,
                  }}
                >
                  {title}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  component="h3"
                  variant="h4"
                  fontWeight={"bold"}
                  sx={{
                    color: "black",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "2.9rem",
                  }}
                >
                  {session?.currentCurrency === "EUR" ? "€" : "$"}
                  {Math.round(price).toLocaleString()}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontSize: "1.1rem",
                      color: "gray",
                      fontWeight: "normal",
                    }}
                  >
                    /{t("landing.month")}
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={billingCycle === "yearly"}
                      onChange={handleBillingCycleToggle}
                      color="primary"
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "#333",
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#666",
                        },
                        "& .Mui-checked": {
                          color: "#3535ff",
                        },
                        "& .Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#3535ff",
                        },
                        transform: "scale(1.2)",
                        ml: 1,
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#333",
                        fontWeight: "bold",
                        fontSize: "1rem",
                      }}
                    >
                      {billingCycle === "yearly"
                        ? t("landing.yearly")
                        : t("landing.monthly")}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              </Box>
              {billingCycle === "yearly" && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    color: "gray",
                    fontWeight: "normal",
                  }}
                >
                  {session?.currentCurrency === "EUR" ? "€" : "$"}
                  {Math.round(fullYearlyPrice).toLocaleString()}{" "}
                  {t("landing.billedYearly")}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mt: 2,
                }}
              >
                <Typography
                  component="p"
                  align="left"
                  sx={{
                    color: "black",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                  }}
                >
                  {t("landing.unlimitedPhotoCredits")}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              bgcolor: highlighted ? "#dcdcff" : "WhiteSmoke",
              minHeight: "5rem",
              borderRadius: "0 0 0.8rem 0.8rem",
              px: 4,
              py: 3,
            }}
          >
            {description.map((feature, index) => (
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                }}
                key={index}
              >
                <CheckIcon color="success" />
                <Typography
                  component="p"
                  variant="body2"
                  align="left"
                  sx={{
                    color: "black",
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color="info"
                size="medium"
                sx={{
                  bgcolor: "#3535ff",
                  color: "white",
                  borderRadius: "0.8rem",
                  fontWeight: "bold",
                  width: "80%",
                  "&:hover": {
                    bgcolor: "#4d4dff",
                  },
                  fontSize: "1.2rem",
                }}
                onClick={handleClick}
              >
                {!loading ? (
                  buttonText
                ) : (
                  <CircularProgress sx={{ color: "white" }} size={20} />
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Fragment>
  );
}
